import { useI18n } from "#hooks/use-i18n"
import type { ImportConfig } from "#lib/indexeddb-import-store"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"

interface CsvConfigurationStepProps {
  config: ImportConfig
  onSeparatorChange: (value: string) => Promise<void>
  onDateFormatChange: (value: string) => void
  onDecimalSeparatorChange: (value: "," | ".") => void
}

export function CsvConfigurationStep({
  config,
  onSeparatorChange,
  onDateFormatChange,
  onDecimalSeparatorChange,
}: CsvConfigurationStepProps) {
  const { t } = useI18n()

  const DATE_FORMATS = [
    { value: "yyyy-MM-dd", label: t("components.expenseImport.dateFormatISO") },
    { value: "dd/MM/yyyy", label: t("components.expenseImport.dateFormatDDMMYYYY") },
    { value: "MM/dd/yyyy", label: t("components.expenseImport.dateFormatMMDDYYYY") },
    { value: "dd-MM-yyyy", label: t("components.expenseImport.dateFormatDDMMYYYYDash") },
    { value: "MM-dd-yyyy", label: t("components.expenseImport.dateFormatMMDDYYYYDash") },
    { value: "yyyy/MM/dd", label: t("components.expenseImport.dateFormatYYYYMMDD") },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{t("components.expenseImport.csvConfiguration")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="separator">{t("components.expenseImport.separatorLabel")}</Label>
            <Select value={config.separator} onValueChange={onSeparatorChange}>
              <SelectTrigger id="separator" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=",">{t("components.expenseImport.separatorComma")}</SelectItem>
                <SelectItem value=";">
                  {t("components.expenseImport.separatorSemicolon")}
                </SelectItem>
                <SelectItem value="\t">{t("components.expenseImport.separatorTab")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dateFormat">{t("components.expenseImport.dateFormatLabel")}</Label>
            <Select value={config.dateFormat} onValueChange={onDateFormatChange}>
              <SelectTrigger id="dateFormat" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMATS.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="decimalSeparator">
              {t("components.expenseImport.decimalSeparatorLabel")}
            </Label>
            <Select value={config.decimalSeparator} onValueChange={onDecimalSeparatorChange}>
              <SelectTrigger id="decimalSeparator" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=".">
                  {t("components.expenseImport.decimalSeparatorDot")}
                </SelectItem>
                <SelectItem value=",">
                  {t("components.expenseImport.decimalSeparatorComma")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
